import * as z from "zod";
import { User } from "../db/models.ts";

/**
 * This is any identifier that can be used to identify the entity that is
 * granting access to the applicaiton. In this case, it is the server instance
 * identifier (e.g.: `serverId`) but the IAM module doesn't need to care about
 * any specifics of that, only that there is some such opaque identifier and
 * that these identifiers are included in access grants for transparency.
 */
// TODO: We should be provide this externally
const AuthenticatorIdentifier = z.string().trim().nonempty();
type AuthenticatorIdentifier = z.output<typeof AuthenticatorIdentifier>;

/** @private to {@module iam} */
export const EmailAddress = z.email();
export type EmailAddress = z.output<typeof EmailAddress>;

/** @private to {@module iam} */
export const Timestamp = z.iso.datetime();
export type Timestamp = z.output<typeof Timestamp>;

export const StringEncodedAccessToken = z.string().trim().nonempty();
export type StringEncodedAccessToken = z.output<typeof StringEncodedAccessToken>;

export const URLSafeEncodedToken = z.base64url().nonempty();
export type URLSafeEncodedToken = z.output<typeof URLSafeEncodedToken>;

export const AccessRequest = z.object({
  signInToken: URLSafeEncodedToken,
});
export interface AccessRequest extends z.output<typeof AccessRequest> {}

export const SignInRequest = z.object({
  userEmailAddress: EmailAddress,
});
export interface SignInRequest extends z.output<typeof SignInRequest> {}

export const TokenSendConfirmation = z.object({
  /**
   * When was the sign-in token sent to the user email address
   */
  tokenSentAt: Timestamp,
});
export interface TokenSendConfirmation extends z.output<typeof TokenSendConfirmation> {}

export const AccessGrant = z.object({
  /**
   * A string-encoded form of some encrypted payload
   *
   * This is typically a JWT but should be treated as opaque by clients and
   * simply exchanged for identity information with the API server.
   */
  accessToken: StringEncodedAccessToken,

  /**
   * A unique identifier for the entity that granted this access.
   *
   * The identifier cannot container leading or trailing whitespace characters
   * and cannot be empty. For an HTTP server, this is the UUID of the server
   * instance.
   *
   * @example "9b369e1e-14d4-42f7-9f4b-a3601cf8bbcc"
   */
  grantedBy: AuthenticatorIdentifier,
});
export interface AccessGrant extends z.output<typeof AccessGrant> {}

export const NotFound = z.object({
  type: z.literal("not found").readonly(),
  identifier: z.string().nonempty(),
  identifierFieldName: z.string().nonempty(),
});
export interface NotFound extends z.output<typeof NotFound> {}

export const NewUserRegistration = z.object({
  /** {@link User.id} of the newly-registered {@link User} */
  newUserId: User.shape.id,

  /** The timestamp of when the user was added to the database */
  registeredAt: z.iso.datetime(),
});
export interface NewUserRegistration extends z.output<typeof NewUserRegistration> {}

export const NewUserRegistrationRequest = z.object({
  /**
   * This is the email address that the new user will use to authenticate
   * themselves. We accept an email address, send a login link with a
   * short-lived one-time use token.
   *
   * If the user cannot access emails sent to this address, they will not be
   * able to login.
   */
  authenticationEmailAddress: z.email(),

  /**
   * What initial attributes should this user have?
   */
  initialUserAttributes: z.object({}).optional(),
});
export interface NewUserRegistrationRequest extends z.output<typeof NewUserRegistrationRequest> {}

export const LoginLinkRequest = z.object({
  /** The {@link EmailAddress} to which the login link should be sent */
  recipientEmailAddress: EmailAddress,
});
export interface LoginLinkRequest extends z.output<typeof LoginLinkRequest> {}

export const LoginLinkSendConfirmation = z.object({
  /** The {@link EmailAddress} to which the login link was sent */
  recipientEmailAddress: EmailAddress,

  /** {@link Timestamp} of when the email was sent */
  sentAt: Timestamp,
});
export interface LoginLinkSendConfirmation extends z.output<typeof LoginLinkSendConfirmation> {}
